import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import ConfirmModal from "../components/ConfirmModal";
import {
  Clock, LogIn, LogOut, CalendarDays, Stethoscope, Plane, BadgeAlert, Bell,
  CheckCircle2, XCircle, Loader2, Inbox, Paperclip, Download, ChevronLeft, ChevronRight,
  FileSpreadsheet, X, Eye, BookOpen, Smartphone, Monitor, Pencil, Trash2, Plus, AlertTriangle, Users,
} from "lucide-react";
import { FilePreviewModal } from "../components/common/FilePreviewModal";
import { UserAvatar } from "../components/UserAvatar";
import { UserCardDialog } from "../components/UserCardDialog";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { isMobileDevice } from "../lib/device";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { fmtDateIT } from "../lib/dateFormat";
import { DateInputIT, TimeInputIT } from "../components/common/DateTimeIT";

// ───────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────

const TYPE_LABEL = {
  permesso: "Permesso orario",
  ferie: "Ferie",
  malattia: "Malattia",
  assenza: "Assenza ingiustificata",
};

const STATUS_LABEL = {
  pending: { txt: "In attesa", className: "wl-badge wl-badge--warning" },
  approved: { txt: "Approvata", className: "wl-badge wl-badge--success" },
  rejected: { txt: "Rifiutata", className: "wl-badge wl-badge--danger" },
  recorded: { txt: "Registrata", className: "wl-badge wl-badge--info" },
  cancelled: { txt: "Annullata", className: "wl-badge" },
};

const TYPE_ICON = {
  permesso: BadgeAlert,
  ferie: Plane,
  malattia: Stethoscope,
  assenza: AlertTriangle,
};

const fmtHM = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";

const fmtDate = (iso) => fmtDateIT(iso);

// Periodo leggibile e senza ambiguità: un giorno solo → "Il giorno …",
// permesso orario → "dalle … alle …" (mai due numeri accostati).
const fmtRecordPeriod = (r) =>
  r.type === "permesso"
    ? `${fmtDate(r.permit_date)} · dalle ${r.permit_time_start} alle ${r.permit_time_end}`
    : r.start_date === r.end_date
      ? `Il giorno ${fmtDate(r.start_date)}`
      : `Dal ${fmtDate(r.start_date)} al ${fmtDate(r.end_date)}`;

const todayISO = () => new Date().toISOString().slice(0, 10);

// Correzioni timbrature (admin)
const EDIT_REASON_OPTIONS = [
  { value: "problema_tecnico", label: "Problema tecnico" },
  { value: "dimenticanza", label: "Dimenticanza" },
  { value: "lavoro_fuori_sede", label: "Lavoro fuori sede" },
  { value: "errore_orario", label: "Errore di orario" },
  { value: "altro", label: "Altro" },
];

const editLine = (e) => {
  const when = `${fmtDate(e.at)} alle ${fmtHM(e.at)}`;
  const tipo = e.clock_type === "in" ? "entrata" : "uscita";
  const motivo = e.reason_label ? e.reason_label.toLowerCase() : "";
  let txt;
  if (e.action === "modified") txt = `Modificata da ${e.edited_by_name} il ${when} — ${motivo} (originale: ${tipo} ${e.before_hhmm} → ${e.after_hhmm})`;
  else if (e.action === "added") txt = `Aggiunta da ${e.edited_by_name} il ${when} — ${motivo} (${tipo} ${e.after_hhmm})`;
  else txt = `Eliminata da ${e.edited_by_name} il ${when} — ${motivo} (${tipo} ${e.before_hhmm})`;
  if (e.note) txt += ` — «${e.note}»`;
  return txt;
};


// ───────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const s = STATUS_LABEL[status] || { txt: status, className: "wl-badge wl-badge--neutral" };
  return (
    <span className={s.className}>
      {s.txt}
    </span>
  );
};

const SectionCard = ({ title, icon: Icon, children, action }) => (
  <div className="bg-surface border border-border rounded-lg p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} strokeWidth={2} className="text-accent" />}
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);


// ───────────────────────────────────────────────────────────────────
// Certificate widget (operator upload / admin download)
// ───────────────────────────────────────────────────────────────────

const CertificateUploader = ({ record, onUploaded }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/attendance/records/${record.id}/certificate`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Certificato allegato");
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
    }
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/jpeg,image/png,image/heic,image/heif,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`cert-input-${record.id}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`cert-upload-${record.id}`}
        className="text-[11px] text-accent hover:underline inline-flex items-center gap-1"
      >
        {uploading ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
        {record.certificate_file_id ? "Sostituisci certificato" : "Allega certificato"}
      </button>
    </>
  );
};

const CertificateDownloader = ({ record, testIdPrefix = "cert-dl" }) => {
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/attendance/records/${record.id}/certificate`, {
        responseType: "blob",
      });
      const ct = record.certificate_content_type || "application/pdf";
      const blob = new Blob([res.data], { type: ct });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = record.certificate_filename || `certificato_${record.id.slice(0, 8)}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download fallito");
    } finally {
      setDownloading(false);
    }
  };
  if (!record.certificate_file_id) return null;
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => setPreview(true)}
        data-testid={`${testIdPrefix}-view-${record.id}`}
        className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
      >
        <Eye size={11} />
        Visualizza
      </button>
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        data-testid={`${testIdPrefix}-${record.id}`}
        className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
      >
        {downloading ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
        Scarica certificato
      </button>
      {preview && (
        <FilePreviewModal
          open
          onClose={() => setPreview(false)}
          filename={record.certificate_filename || `certificato_${record.id.slice(0, 8)}.pdf`}
          fetchPath={`/attendance/records/${record.id}/certificate?inline=1`}
          onDownload={download}
        />
      )}
    </span>
  );
};


// ───────────────────────────────────────────────────────────────────
// MyClockToday — bottoni Entro/Esco
// ───────────────────────────────────────────────────────────────────

const MyClockToday = ({ initial }) => {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, present: false, clocks: [] });
  const clockAllowed = !isMobileDevice() || !!user?.can_clock_mobile;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/clock/me/today");
      setState({ loading: false, present: !!data.present, clocks: data.clocks || [] });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      toast.error("Impossibile caricare lo stato presenza");
    }
  }, []);

  // I dati di apertura arrivano dalla chiamata unica /attendance/pagina;
  // refresh() serve solo dopo un'azione (entrata/uscita).
  useEffect(() => {
    if (initial) setState({ loading: false, present: !!initial.present, clocks: initial.clocks || [] });
  }, [initial]);

  const clockIn = async () => {
    try {
      await api.post("/attendance/clock/in");
      toast.success("Entrata registrata");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel timbrare l'entrata");
    }
  };
  const clockOut = async () => {
    try {
      await api.post("/attendance/clock/out");
      toast.success("Uscita registrata");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel timbrare l'uscita");
    }
  };

  return (
    <SectionCard title="Oggi" icon={Clock}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${state.present ? "bg-green-500" : "bg-gray-300"}`} />
          <span className="text-sm font-medium" data-testid="presence-status">
            {state.loading ? "Caricamento…" : state.present ? "Presente" : "Non in sede"}
          </span>
          {state.present && state.clocks.length > 0 && (
            <span className="text-xs text-text-muted ml-2">
              Ultima entrata alle {fmtHM(state.clocks.filter(c => c.type === "in").slice(-1)[0]?.at)}
            </span>
          )}
        </div>
        {/* Bottoni: mobile full-width grid 2 colonne; desktop compatti inline.
            Da dispositivo mobile la timbratura è bloccata (salvo eccezione per-utente). */}
        {clockAllowed ? (
          <div className="grid grid-cols-2 gap-2 md:flex md:gap-2">
            <Button
              onClick={clockIn}
              disabled={state.loading || state.present}
              data-testid="clock-in-btn"
              className="h-14 md:h-9 text-base md:text-sm font-semibold md:font-medium bg-green-600 hover:bg-green-700 disabled:bg-green-600/40 text-white"
            >
              <LogIn size={20} className="mr-2 md:mr-1.5 md:size-4" /> Entro
            </Button>
            <Button
              onClick={clockOut}
              disabled={state.loading || !state.present}
              data-testid="clock-out-btn"
              className="h-14 md:h-9 text-base md:text-sm font-semibold md:font-medium bg-gray-700 hover:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-500 text-white md:bg-transparent md:hover:bg-subtle md:text-text-primary md:border md:border-border"
            >
              <LogOut size={20} className="mr-2 md:mr-1.5 md:size-4" /> Esco
            </Button>
          </div>
        ) : (
          <div className="text-sm px-3 py-2.5 rounded-lg bg-subtle border border-border inline-flex items-center gap-2 text-text-secondary"
               data-testid="attendance-mobile-note">
            <Monitor size={15} className="flex-shrink-0" /> La timbratura si fa dal computer
          </div>
        )}
      </div>
      {state.clocks.length > 0 && (
        <div className="border-t border-border pt-3">
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Timbrature di oggi</div>
          <ul className="text-sm space-y-1">
            {state.clocks.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-text-secondary">
                {c.type === "in" ? <LogIn size={13} className="text-green-600" /> : <LogOut size={13} className="text-text-muted" />}
                <span className="font-mono">{fmtHM(c.at)}</span>
                <span>{c.type === "in" ? "entrata" : "uscita"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
};


// ───────────────────────────────────────────────────────────────────
// Form crea segnalazione (operator + admin)
// ───────────────────────────────────────────────────────────────────

const CreateRecordForm = ({ onCreated }) => {
  const [type, setType] = useState("permesso");
  const [permDate, setPermDate] = useState(todayISO());
  const [permStart, setPermStart] = useState("14:00");
  const [permEnd, setPermEnd] = useState("16:00");
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  /**
   * Validazione lato client — messaggi actionable, mai vaghi né colpevolizzanti.
   * Restituisce oggetto { campo: "messaggio" } o {} se tutto ok.
   */
  const validate = () => {
    const errs = {};
    if (type === "permesso") {
      if (!permDate) errs.permDate = "Indica la data del permesso.";
      if (!permStart) errs.permStart = "Indica l'ora di inizio.";
      if (!permEnd) errs.permEnd = "Indica l'ora di fine.";
      if (permStart && permEnd && permEnd <= permStart) {
        errs.permEnd = "L'ora di fine deve essere successiva a quella di inizio.";
      }
    } else {
      if (!startDate) errs.startDate = "Indica la data iniziale.";
      if (!endDate) errs.endDate = "Indica la data finale.";
      if (startDate && endDate && endDate < startDate) {
        errs.endDate = "La data finale non può precedere quella iniziale.";
      }
    }
    return errs;
  };

  const submit = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      const payload = { type, note: note || undefined };
      if (type === "permesso") {
        Object.assign(payload, { permit_date: permDate, permit_time_start: permStart, permit_time_end: permEnd });
      } else {
        Object.assign(payload, { start_date: startDate, end_date: endDate });
      }
      const { data } = await api.post("/attendance/records", payload);
      toast.success(`${TYPE_LABEL[type]} ${data.status === "pending" ? "inviata" : "registrata"}`);
      setNote("");
      setErrors({});
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  const notePlaceholder =
    type === "malattia"
      ? "es. Impossibilitato al lavoro fino al 10/10"
      : "es. Visita medica in fascia mattutina";

  return (
    <SectionCard title="Crea segnalazione" icon={CalendarDays}>
      <div className="flex gap-2 mb-4">
        {["permesso", "ferie", "malattia"].map((t) => {
          const Icon = TYPE_ICON[t];
          return (
            <button
              key={t}
              type="button"
              onClick={() => { setType(t); setErrors({}); }}
              data-testid={`tab-${t}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                type === t
                  ? "bg-accent text-white"
                  : "bg-subtle text-text-secondary hover:bg-border"
              }`}
            >
              <Icon size={14} /> {TYPE_LABEL[t]}
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {type === "permesso" ? (
          <>
            <div className="wl-field">
              <label htmlFor="perm-date" className="wl-label wl-label--required">Data</label>
              <DateInputIT
                id="perm-date"
                value={permDate}
                onChange={setPermDate}
                disabled={submitting}
                invalid={!!errors.permDate}
                testId="permesso-date"
              />
              {errors.permDate && (
                <span id="perm-date-err" className="wl-error-msg" data-testid="permesso-date-error">{errors.permDate}</span>
              )}
            </div>
            <div className="wl-field-row wl-field-row--2col">
              <div className="wl-field">
                <label htmlFor="perm-start" className="wl-label wl-label--required">Ora inizio</label>
                <TimeInputIT
                  id="perm-start"
                  value={permStart}
                  onChange={setPermStart}
                  disabled={submitting}
                  invalid={!!errors.permStart}
                  testId="permesso-start"
                />
                {errors.permStart && (
                  <span className="wl-error-msg" data-testid="permesso-start-error">{errors.permStart}</span>
                )}
              </div>
              <div className="wl-field">
                <label htmlFor="perm-end" className="wl-label wl-label--required">Ora fine</label>
                <TimeInputIT
                  id="perm-end"
                  value={permEnd}
                  onChange={setPermEnd}
                  disabled={submitting}
                  invalid={!!errors.permEnd}
                  testId="permesso-end"
                />
                {errors.permEnd && (
                  <span className="wl-error-msg" data-testid="permesso-end-error">{errors.permEnd}</span>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="wl-field-row wl-field-row--2col">
            <div className="wl-field">
              <label htmlFor="start-date" className="wl-label wl-label--required">Dal</label>
              <DateInputIT
                id="start-date"
                value={startDate}
                onChange={(v) => { setStartDate(v); if (v && endDate && endDate < v) setEndDate(v); }}
                disabled={submitting}
                invalid={!!errors.startDate}
                testId="start-date"
              />
              {errors.startDate && (
                <span className="wl-error-msg" data-testid="start-date-error">{errors.startDate}</span>
              )}
            </div>
            <div className="wl-field">
              <label htmlFor="end-date" className="wl-label wl-label--required">Al</label>
              <DateInputIT
                id="end-date"
                value={endDate}
                min={startDate}
                onChange={setEndDate}
                disabled={submitting}
                invalid={!!errors.endDate}
                testId="end-date"
              />
              {errors.endDate && (
                <span className="wl-error-msg" data-testid="end-date-error">{errors.endDate}</span>
              )}
            </div>
          </div>
        )}
        <div className="wl-field">
          <label htmlFor="note" className="wl-label">Nota</label>
          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            disabled={submitting}
            placeholder={notePlaceholder}
            className="wl-textarea"
            data-testid="note-input"
          />
          <span className="wl-helper wl-helper--muted">
            {type === "malattia" ? "Facoltativo. Non inserire informazioni cliniche." : "Facoltativo. Motivazione visibile agli amministratori."}
          </span>
        </div>
        <div className="flex justify-stretch md:justify-end">
          <Button onClick={submit} disabled={submitting} data-testid="submit-record" className="w-full md:w-auto h-11 md:h-9">
            {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
            {type === "malattia" ? "Comunica" : "Invia richiesta"}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
};


// ───────────────────────────────────────────────────────────────────
// MyRecordsList — operatore vede sue
// ───────────────────────────────────────────────────────────────────

const MyRecordsList = ({ items }) => {
  const loading = items == null;

  if (loading) return <div className="text-sm text-text-muted">Caricamento…</div>;
  if (!items.length) return <div className="text-sm text-text-muted py-4 text-center"><Inbox size={20} className="mx-auto mb-1 opacity-60" />Nessuna segnalazione</div>;

  return (
    <ul className="divide-y divide-border" data-testid="my-records-list">
      {items.map((r) => {
        const Icon = TYPE_ICON[r.type] || CalendarDays;
        return (
          <li key={r.id} className="py-3 flex items-start gap-3">
            <Icon size={16} className="text-text-muted mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-medium text-text-primary">{TYPE_LABEL[r.type]}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="text-xs text-text-secondary mt-0.5">
                {fmtRecordPeriod(r)}
              </div>
              {r.note && <div className="text-xs text-text-muted mt-1 italic">«{r.note}»</div>}
              {r.decision_note && <div className="text-xs text-text-muted mt-1">Nota admin: {r.decision_note}</div>}
              {r.type === "malattia" && (
                <div className="flex items-center gap-3 mt-1.5">
                  {r.certificate_file_id && <CertificateDownloader record={r} testIdPrefix="my-cert-dl" />}
                  <CertificateUploader record={r} onUploaded={() => window.location.reload()} />
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};


// ───────────────────────────────────────────────────────────────────
// TeamCalendar — vista admin mensile colpo d'occhio
// ───────────────────────────────────────────────────────────────────

const ABSENCE_COLORS = {
  ferie: { bg: "bg-blue-500", letter: "F", label: "Ferie" },
  malattia: { bg: "bg-red-500", letter: "M", label: "Malattia" },
  permesso: { bg: "bg-amber-500", letter: "P", label: "Permesso orario" },
  assenza: { bg: "bg-slate-600", letter: "A", label: "Assenza ingiustificata" },
};

// ───────────────────────────────────────────────────────────────────
// Correzioni timbrature (admin): dettaglio giornata + modifica/aggiunta/eliminazione
// ───────────────────────────────────────────────────────────────────

const ReasonFields = ({ reason, setReason, note, setNote, idPrefix }) => (
  <div className="grid sm:grid-cols-2 gap-2 mt-2">
    <select
      value={reason}
      onChange={(e) => setReason(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm w-full"
      data-testid={`${idPrefix}-reason-select`}
    >
      <option value="">Motivo (obbligatorio)…</option>
      {EDIT_REASON_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    <input
      type="text"
      value={note}
      onChange={(e) => setNote(e.target.value)}
      placeholder="Nota (facoltativa)"
      maxLength={300}
      className="h-9 rounded-md border border-border bg-surface px-3 text-sm w-full"
      data-testid={`${idPrefix}-note-input`}
    />
  </div>
);

const DayDetailDialog = ({ userId, userName, date, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [panel, setPanel] = useState(null); // { mode: 'edit'|'delete'|'add', clock? }
  const [time, setTime] = useState("");
  const [timeIn, setTimeIn] = useState("");
  const [timeOut, setTimeOut] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/attendance/day?user_id=${userId}&date=${date}`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Errore nel caricamento della giornata.");
        onClose();
      });
  }, [userId, date, onClose]);

  useEffect(() => { load(); }, [load]);

  const resetPanel = () => { setPanel(null); setTime(""); setTimeIn(""); setTimeOut(""); setReason(""); setNote(""); };

  const openEdit = (clock) => { resetPanel(); setPanel({ mode: "edit", clock }); setTime(clock.hhmm); };
  const openDelete = (clock) => { resetPanel(); setPanel({ mode: "delete", clock }); };
  const openAdd = () => { resetPanel(); setPanel({ mode: "add" }); };

  const afterSave = (res) => {
    toast.success("Correzione registrata.");
    if (res?.data?.warning) toast.warning(res.data.warning);
    resetPanel();
    load();
    onChanged?.();
  };

  const submit = async () => {
    if (!reason) { toast.error("Il motivo è obbligatorio."); return; }
    setSaving(true);
    try {
      if (panel.mode === "edit") {
        if (!time) { toast.error("Indica il nuovo orario."); setSaving(false); return; }
        const res = await api.put(`/attendance/clocks/${panel.clock.id}`, { time, reason, note: note || null });
        afterSave(res);
      } else if (panel.mode === "delete") {
        const res = await api.post(`/attendance/clocks/${panel.clock.id}/delete`, { reason, note: note || null });
        afterSave(res);
      } else {
        if (!timeIn && !timeOut) { toast.error("Indica almeno un orario."); setSaving(false); return; }
        const res = await api.post("/attendance/clocks/manual", {
          user_id: userId, date, time_in: timeIn || null, time_out: timeOut || null, reason, note: note || null,
        });
        afterSave(res);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita.");
    } finally {
      setSaving(false);
    }
  };

  const activeClocks = (data?.clocks || []).filter((c) => !c.deleted);
  const deletedClocks = (data?.clocks || []).filter((c) => c.deleted);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="day-detail-dialog">
      <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-surface z-10">
          <div>
            <div className="font-semibold text-text-primary text-sm">{userName}</div>
            <div className="text-xs text-text-muted">{fmtDate(date)} · Dettaglio giornata</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-subtle" data-testid="day-detail-close">
            <X size={16} />
          </button>
        </div>

        {!data ? (
          <div className="p-5 text-sm text-text-muted">Caricamento…</div>
        ) : (
          <div className="p-5 space-y-4">
            {data.warning && (
              <div className="flex items-start gap-2 text-xs rounded-md border border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-3 py-2" data-testid="day-overwork-warning">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {data.warning}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-text-muted">
                  Timbrature · ore lavorate {data.worked_hhmm}
                </div>
                <button
                  type="button"
                  onClick={openAdd}
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                  data-testid="day-add-clock-btn"
                >
                  <Plus size={12} /> Aggiungi timbratura
                </button>
              </div>
              {activeClocks.length === 0 && deletedClocks.length === 0 ? (
                <div className="text-sm text-text-muted py-2">Nessuna timbratura in questa giornata.</div>
              ) : (
                <ul className="space-y-1.5">
                  {activeClocks.map((c) => (
                    <li key={c.id} className="flex items-center gap-2 text-sm rounded-md border border-border px-3 py-2" data-testid={`day-clock-${c.id}`}>
                      {c.type === "in"
                        ? <LogIn size={14} className="text-green-600 shrink-0" />
                        : <LogOut size={14} className="text-text-muted shrink-0" />}
                      <span className="font-medium text-text-primary">{c.type === "in" ? "Entrata" : "Uscita"}</span>
                      <span className="font-mono text-text-primary">{c.hhmm}</span>
                      {c.edited && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600" title={c.original_hhmm ? `Orario originale: ${c.original_hhmm}` : "Aggiunta manualmente"}>
                          <Pencil size={10} />
                          {c.original_hhmm && c.original_hhmm !== c.hhmm ? `orig. ${c.original_hhmm}` : c.manual ? "manuale" : "corretta"}
                        </span>
                      )}
                      <span className="flex-1" />
                      <button type="button" onClick={() => openEdit(c)} className="p-1 rounded hover:bg-subtle text-text-muted hover:text-accent" title="Modifica orario" data-testid={`day-clock-edit-${c.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button type="button" onClick={() => openDelete(c)} className="p-1 rounded hover:bg-subtle text-text-muted hover:text-red-500" title="Elimina timbratura" data-testid={`day-clock-delete-${c.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </li>
                  ))}
                  {deletedClocks.map((c) => (
                    <li key={c.id} className="flex items-center gap-2 text-sm rounded-md border border-dashed border-border px-3 py-2 opacity-60" data-testid={`day-clock-deleted-${c.id}`}>
                      <Trash2 size={13} className="text-red-400 shrink-0" />
                      <span className="line-through">{c.type === "in" ? "Entrata" : "Uscita"} {c.hhmm}</span>
                      <span className="text-[11px] text-text-muted">eliminata (dato conservato)</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {panel && (
              <div className="rounded-md border border-accent/40 bg-accent/5 p-3" data-testid="day-action-panel">
                <div className="text-sm font-medium text-text-primary mb-1">
                  {panel.mode === "edit" && <>Modifica {panel.clock.type === "in" ? "entrata" : "uscita"} delle {panel.clock.hhmm}</>}
                  {panel.mode === "delete" && <>Elimina {panel.clock.type === "in" ? "entrata" : "uscita"} delle {panel.clock.hhmm}</>}
                  {panel.mode === "add" && <>Aggiungi timbratura del {fmtDate(date)}</>}
                </div>
                {panel.mode === "edit" && (
                  <TimeInputIT
                    value={time}
                    onChange={setTime}
                    className="h-9 w-28"
                    testId="edit-time-input"
                  />
                )}
                {panel.mode === "add" && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-text-muted">Entrata</label>
                    <TimeInputIT value={timeIn} onChange={setTimeIn} className="h-9 w-24" testId="add-time-in" />
                    <label className="text-xs text-text-muted">Uscita</label>
                    <TimeInputIT value={timeOut} onChange={setTimeOut} className="h-9 w-24" testId="add-time-out" />
                  </div>
                )}
                {panel.mode === "delete" && (
                  <div className="text-xs text-text-muted">
                    La timbratura viene solo nascosta: l'orario originale resta salvato nello storico.
                  </div>
                )}
                <ReasonFields reason={reason} setReason={setReason} note={note} setNote={setNote} idPrefix="clock-fix" />
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="ghost" size="sm" onClick={resetPanel} data-testid="clock-fix-cancel">Annulla</Button>
                  <Button
                    size="sm"
                    onClick={submit}
                    disabled={saving}
                    className={panel.mode === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                    data-testid="clock-fix-save"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : panel.mode === "delete" ? "Elimina" : "Salva"}
                  </Button>
                </div>
              </div>
            )}

            {(data.edits || []).length > 0 && (
              <div data-testid="day-edits-history">
                <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Storico modifiche</div>
                <ul className="space-y-1">
                  {data.edits.map((e) => (
                    <li key={e.id} className="flex items-start gap-2 text-xs text-text-secondary">
                      <Pencil size={11} className="text-amber-500 mt-0.5 shrink-0" />
                      {editLine(e)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Avviso per il dipendente: timbrature corrette dall'amministratore
const EmployeeClockNotices = ({ initial }) => {
  const [data, setData] = useState(null);

  const load = () => {
    api.get("/attendance/notifications/user/me")
      .then(({ data }) => setData(data))
      .catch(() => {});
  };
  useEffect(() => { if (initial) setData(initial); }, [initial]);

  if (!data || data.unread === 0) return null;
  const unreadItems = data.items.filter((i) => !i.read_at);

  const markRead = async () => {
    try {
      await api.post("/attendance/notifications/user/read-all");
      load();
    } catch { /* noop */ }
  };

  return (
    <div className="mb-5 rounded-lg border border-amber-400/50 bg-amber-500/10 p-4" data-testid="clock-edit-notices">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Bell size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-text-primary mb-1">Comunicazioni dall'amministratore</div>
            <ul className="space-y-1">
              {unreadItems.map((i) => (
                <li key={i.id} className="text-sm text-text-secondary" data-testid={`clock-notice-${i.id}`}>
                  {i.message}
                  <span className="text-[11px] text-text-muted ml-1.5">{fmtDate(i.created_at)} {fmtHM(i.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={markRead} data-testid="clock-notices-read-btn">
          Ho letto
        </Button>
      </div>
    </div>
  );
};


const TeamCalendar = ({ refreshKey, year, month, onPrev, onNext, onDataChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { userId, userName, date }

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/attendance/calendar?year=${year}&month=${month}`).then(({ data }) => {
      setData(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [year, month]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const monthName = new Date(year, month - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const monthLabel = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  const todayISOstr = todayISO();

  return (
    <SectionCard
      title="Calendario team"
      icon={CalendarDays}
      action={
        <div className="flex items-center gap-2 text-sm">
          <button onClick={onPrev} className="p-1 rounded hover:bg-subtle" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <span className="font-medium text-text-primary min-w-[140px] text-center">{monthLabel}</span>
          <button onClick={onNext} className="p-1 rounded hover:bg-subtle" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
      }
    >
      {loading || !data ? (
        <div className="text-sm text-text-muted py-3">Caricamento…</div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-1 px-1 pb-1" data-testid="team-calendar">
            <table className="text-[11px] border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-surface text-left font-medium text-text-secondary pr-2 pb-2 min-w-[140px]">Membro</th>
                  {data.days.map((d, i) => {
                    const wd = data.weekdays[i];
                    const dayNum = parseInt(d.slice(-2), 10);
                    const isWeekend = wd === 5 || wd === 6;
                    const isToday = d === todayISOstr;
                    return (
                      <th
                        key={d}
                        className={`text-center font-medium pb-2 px-1 min-w-[24px] ${isToday ? "text-accent" : isWeekend ? "text-text-muted" : "text-text-secondary"}`}
                      >
                        <div>{dayNum}</div>
                        <div className="text-[9px] uppercase">{["L", "M", "M", "G", "V", "S", "D"][wd]}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Chi non ha né presenze né assenze nel mese va in fondo,
                  // sotto un separatore: niente righe vuote in mezzo al calendario.
                  const haDati = (u) => Object.values(u.cells || {}).some((c) => c && (c.first_in || (c.absences || []).length > 0));
                  const attivi = data.team.filter(haDati);
                  const vuoti = data.team.filter((u) => !haDati(u));
                  return [...attivi, ...(vuoti.length ? ["__sep__"] : []), ...vuoti];
                })().map((u) => u === "__sep__" ? (
                  <tr key="separatore-vuoti">
                    <td colSpan={(data.days?.length || 0) + 1}
                        className="sticky left-0 z-10 bg-surface pt-3 pb-1 text-[10px] uppercase tracking-wider text-text-muted"
                        data-testid="cal-senza-presenze">
                      Senza presenze né assenze nel mese
                    </td>
                  </tr>
                ) : (
                  <tr key={u.user_id} className="border-t border-border" data-testid={`cal-row-${u.user_id}`}>
                    <td className="sticky left-0 z-10 bg-surface pr-2 py-1 text-text-primary truncate">
                      {u.user_name}
                      <span className="text-[9px] text-text-muted ml-1">{u.role === "admin" ? "·a" : u.role === "super_admin" ? "·sa" : ""}</span>
                    </td>
                    {data.days.map((d, i) => {
                      const cell = u.cells[d];
                      const wd = data.weekdays[i];
                      const isWeekend = wd === 5 || wd === 6;
                      let bg = isWeekend ? "bg-subtle/30" : "bg-transparent";
                      let letter = "";
                      let tooltip = d;
                      if (cell) {
                        if (cell.absences && cell.absences.length > 0) {
                          const a = cell.absences[0];
                          const colorCfg = ABSENCE_COLORS[a.type];
                          if (colorCfg) {
                            bg = colorCfg.bg + "/85 text-white";
                            letter = colorCfg.letter;
                            tooltip = `${d} · ${colorCfg.label}${a.note ? ` — "${a.note}"` : ""}`;
                          }
                        } else if (cell.first_in) {
                          bg = "bg-green-500/85 text-white";
                          letter = "•";
                          const inT = new Date(cell.first_in).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                          const outT = cell.last_out ? new Date(cell.last_out).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "in corso";
                          tooltip = `${d} · entrata ${inT} · uscita ${outT}`;
                        }
                        if (cell.edited && cell.edits?.length > 0) {
                          tooltip += "\n" + cell.edits.map(editLine).join("\n");
                        }
                      }
                      return (
                        <td
                          key={d}
                          title={tooltip}
                          className={`text-center align-middle font-semibold rounded ${bg}`}
                          style={{ width: 24, height: 24, padding: 0 }}
                        >
                          <button
                            type="button"
                            onClick={() => setDetail({ userId: u.user_id, userName: u.user_name, date: d })}
                            className="relative w-6 h-6 inline-flex items-center justify-center cursor-pointer"
                            data-testid={`cal-cell-${u.user_id}-${d}`}
                            aria-label={`Dettaglio ${u.user_name} ${d}`}
                          >
                            {letter}
                            {cell?.edited && (
                              <Pencil size={8} className="absolute -top-0.5 -right-0.5 text-amber-500" strokeWidth={2.5} />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-text-muted">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-500/85" /> presenza</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-500/85" /> ferie (F)</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-500/85" /> malattia (M)</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-500/85" /> permesso (P)</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-slate-600/85" /> assenza ingiustificata (A)</span>
            <span className="inline-flex items-center gap-1"><Pencil size={10} className="text-amber-500" /> giornata corretta</span>
          </div>
        </>
      )}
      {detail && (
        <DayDetailDialog
          userId={detail.userId}
          userName={detail.userName}
          date={detail.date}
          onClose={() => setDetail(null)}
          onChanged={() => { load(); onDataChanged?.(); }}
        />
      )}
    </SectionCard>
  );
};


// ───────────────────────────────────────────────────────────────────
// MonthDiary — diario eventi assenze del mese (vista admin)
// ───────────────────────────────────────────────────────────────────

const fmtPeriod = (e) =>
  e.type === "permesso"
    ? `${fmtDate(e.permit_date)} · dalle ${e.permit_time_start} alle ${e.permit_time_end}`
    : e.start_date === e.end_date
      ? `il giorno ${fmtDate(e.start_date)}`
      : `dal ${fmtDate(e.start_date)} al ${fmtDate(e.end_date)}`;

const MonthDiary = ({ year, month, refreshKey }) => {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/attendance/diary?year=${year}&month=${month}`).then(({ data }) => {
      if (!cancelled) setEvents(data.events || []);
    }).catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, [year, month, refreshKey]);

  const byDay = useMemo(() => {
    const m = new Map();
    (events || []).forEach((e) => {
      if (!m.has(e.local_date)) m.set(e.local_date, []);
      m.get(e.local_date).push(e);
    });
    return [...m.entries()];
  }, [events]);

  const dayLabel = (iso) => {
    const s = new Date(iso + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  return (
    <SectionCard title="Diario del mese" icon={BookOpen}>
      {events === null ? (
        <div className="text-sm text-text-muted">Caricamento…</div>
      ) : byDay.length === 0 ? (
        <div className="text-sm text-text-muted py-4 text-center" data-testid="diary-empty">
          <Inbox size={20} className="mx-auto mb-1 opacity-60" />Nessun evento nel mese.
        </div>
      ) : (
        <div className="space-y-4" data-testid="month-diary">
          {byDay.map(([day, list]) => (
            <div key={day} data-testid={`diary-day-${day}`}>
              <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1.5">{dayLabel(day)}</div>
              <ul className="space-y-1.5">
                {list.map((e, i) => {
                  if (e.kind === "clock_edit") {
                    const tipo = e.clock_type === "in" ? "l'entrata" : "l'uscita";
                    return (
                      <li key={`${e.record_id}-${e.kind}-${i}`} className="flex items-start gap-2 text-sm"
                          data-testid={`diary-event-${e.record_id}-clock_edit`}>
                        <Pencil size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1 text-text-secondary leading-relaxed">
                          <span className="font-medium text-text-primary">{e.edited_by_name}</span>{" "}
                          {e.action === "modified" && (
                            <>ha corretto {tipo} di <span className="font-medium text-text-primary">{e.user_name}</span> del {fmtDate(e.target_date)}: {e.before_hhmm} → {e.after_hhmm}</>
                          )}
                          {e.action === "added" && (
                            <>ha aggiunto {tipo} per <span className="font-medium text-text-primary">{e.user_name}</span> del {fmtDate(e.target_date)}: {e.after_hhmm}</>
                          )}
                          {e.action === "deleted" && (
                            <>ha eliminato {tipo} di <span className="font-medium text-text-primary">{e.user_name}</span> del {fmtDate(e.target_date)}: {e.before_hhmm}</>
                          )}
                          <span className="text-text-muted"> — {(e.reason_label || "").toLowerCase()}</span>
                          {e.note && <span className="text-xs text-text-muted italic"> — «{e.note}»</span>}
                          <span className="text-[11px] text-text-muted ml-1.5">{fmtHM(e.at)}</span>
                        </div>
                      </li>
                    );
                  }
                  if (e.kind === "mobile_blocked") {
                    return (
                      <li key={`${e.record_id}-${e.kind}-${i}`} className="flex items-start gap-2 text-sm"
                          data-testid={`diary-event-${e.record_id}-mobile_blocked`}>
                        <Smartphone size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1 text-text-secondary leading-relaxed">
                          <span className="font-medium text-text-primary">{e.user_name}</span>{" "}
                          ha provato a timbrare {e.type === "in" ? "l'entrata" : "l'uscita"} da un dispositivo mobile
                          <span className="wl-badge wl-badge--danger ml-1.5" style={{ fontSize: "10px" }}>bloccata</span>
                          <span className="text-[11px] text-text-muted ml-1.5">{fmtHM(e.at)}</span>
                        </div>
                      </li>
                    );
                  }
                  const Icon = e.kind === "decision"
                    ? (e.decision === "approved" ? CheckCircle2 : XCircle)
                    : (TYPE_ICON[e.type] || CalendarDays);
                  const iconCls = e.kind === "decision"
                    ? (e.decision === "approved" ? "text-green-600" : "text-red-500")
                    : "text-text-muted";
                  return (
                    <li key={`${e.record_id}-${e.kind}-${i}`} className="flex items-start gap-2 text-sm"
                        data-testid={`diary-event-${e.record_id}-${e.kind}`}>
                      <Icon size={14} className={`${iconCls} mt-0.5 flex-shrink-0`} />
                      <div className="min-w-0 flex-1 text-text-secondary leading-relaxed">
                        {e.kind === "decision" ? (
                          <>
                            <span className="font-medium text-text-primary">{e.decided_by}</span>{" "}
                            ha {e.decision === "approved" ? "approvato" : "rifiutato"} la richiesta di{" "}
                            <span className="text-text-primary">{(TYPE_LABEL[e.type] || e.type).toLowerCase()}</span> di{" "}
                            <span className="font-medium text-text-primary">{e.user_name}</span>{" "}
                            <span className="text-text-muted">({fmtPeriod(e)})</span>
                            {e.decision_note && <span className="text-xs text-text-muted italic"> — «{e.decision_note}»</span>}
                          </>
                        ) : (
                          <>
                            <span className="font-medium text-text-primary">{e.user_name}</span>{" "}
                            {e.kind === "sickness" ? "ha comunicato" : e.auto_approved ? "ha registrato" : "ha richiesto"}{" "}
                            <span className="text-text-primary">{TYPE_LABEL[e.type] || e.type}</span>{" "}
                            <span className="text-text-muted">({fmtPeriod(e)})</span>
                            {e.auto_approved && <span className="wl-badge wl-badge--success ml-1.5" style={{ fontSize: "10px" }}>auto-approvata</span>}
                            {!e.auto_approved && e.kind === "request" && e.status === "pending" && (
                              <span className="wl-badge wl-badge--warning ml-1.5" style={{ fontSize: "10px" }}>in attesa</span>
                            )}
                            {e.has_certificate && <span className="text-xs text-text-muted"> · certificato allegato</span>}
                            {e.note && <span className="text-xs text-text-muted italic"> — «{e.note}»</span>}
                          </>
                        )}
                        <span className="text-[11px] text-text-muted ml-1.5">{fmtHM(e.at)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};


// ───────────────────────────────────────────────────────────────────
// ADMIN VIEW
// ───────────────────────────────────────────────────────────────────

/**
 * Deriva stato e rank di ordinamento da un elemento team-today.
 * Rank: 0 = presente (attualmente sul lavoro), 1 = non timbrato, 2 = uscito.
 * Fra i presenti ordina per orario di entrata crescente; fra usciti/non
 * timbrati ordina per cognome alfabetico.
 */
const deriveTeamRowState = (u) => {
  const hasIn = !!u.last_in;
  const hasOut = !!u.last_out;
  if (hasIn && !hasOut) return { key: "present", label: "Presente", variant: "success", rank: 0 };
  if (!hasIn)           return { key: "off",     label: "Non timbrato", variant: "warning", rank: 1 };
  return                       { key: "out",     label: "Uscito/a", variant: "neutral", rank: 2 };
};

const roleLabel = (role) => (
  role === "admin" ? "Amministratore" :
  role === "super_admin" ? "Super Admin" :
  "Operatore"
);

const AdminTeamToday = ({ initial, onDataChanged }) => {
  const [team, setTeam] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [openUserId, setOpenUserId] = useState(null);
  const [fixUser, setFixUser] = useState(null); // { user_id, user_name }
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get("/attendance/clock/team/today").then(({ data }) => {
      setTeam(data?.team || []);
      setCollaborators(data?.collaborators || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (initial) { setTeam(initial.team || []); setCollaborators(initial.collaborators || []); setLoading(false); }
  }, [initial]);

  // Ordinamento: presenti per orario entrata, non timbrati alfabetico, poi
  // usciti per orario di uscita più recente.
  const sortedTeam = useMemo(() => {
    return [...team].sort((a, b) => {
      const sa = deriveTeamRowState(a);
      const sb = deriveTeamRowState(b);
      if (sa.rank !== sb.rank) return sa.rank - sb.rank;
      if (sa.rank === 0) {
        return new Date(a.last_in.at) - new Date(b.last_in.at);
      }
      return String(a.user_name || "").localeCompare(String(b.user_name || ""), "it");
    });
  }, [team]);

  if (loading) return <div className="text-sm text-text-muted">Caricamento team…</div>;

  const presentCount = team.filter((t) => deriveTeamRowState(t).key === "present").length;

  return (
    <SectionCard title={`Team oggi · ${presentCount}/${team.length} presenti`} icon={Clock}>
      <ul className="wl-team-list" data-testid="team-list">
        {sortedTeam.map((u) => {
          const state = deriveTeamRowState(u);
          return (
            <li key={u.user_id} className="wl-team-row" data-testid={`team-row-${u.user_id}`}>
              <button
                type="button"
                onClick={() => setOpenUserId(u.user_id)}
                className="shrink-0 rounded-full transition-transform hover:scale-105"
                data-testid={`team-avatar-${u.user_id}`}
                aria-label={`Scheda di ${u.user_name}`}
              >
                <UserAvatar userId={u.user_id} name={u.user_name} version={u.avatar_version ?? null} size={32} />
              </button>
              <div className="wl-team-row__meta">
                <button
                  type="button"
                  onClick={() => setOpenUserId(u.user_id)}
                  className="wl-team-row__name text-left hover:underline"
                  title={u.user_name}
                >
                  {u.user_name}
                </button>
                <span className="wl-team-row__role">{(u.role === "admin" || u.role === "super_admin") ? roleLabel(u.role) : (u.job_title || roleLabel(u.role))}</span>
              </div>
              <div className="wl-team-row__right">
                <button
                  type="button"
                  onClick={() => setFixUser({ user_id: u.user_id, user_name: u.user_name })}
                  className={`p-1 rounded hover:bg-subtle ${u.edited_today ? "text-amber-500" : "text-text-muted hover:text-accent"}`}
                  title={u.edited_today ? "Giornata corretta — apri il dettaglio" : "Correggi le timbrature di oggi"}
                  data-testid={`team-fix-${u.user_id}`}
                >
                  <Pencil size={13} />
                </button>
                <span className={`wl-badge wl-badge--${state.variant}`} data-testid={`team-row-${u.user_id}-status`}>
                  {state.label}
                </span>
                <span className={`wl-team-row__times ${state.key === "off" ? "wl-team-row__times--muted" : ""}`}>
                  {state.key === "off" && "—"}
                  {state.key === "present" && (
                    <>entrata {fmtHM(u.last_in.at)}</>
                  )}
                  {state.key === "out" && (
                    <>
                      {fmtHM(u.last_in.at)}
                      <span className="wl-team-row__times-sep">→</span>
                      {fmtHM(u.last_out.at)}
                    </>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Collaboratori esterni: non soggetti a timbratura, senza stato presenza */}
      {collaborators.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="collaborators-section">
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">
            Collaboratori · non soggetti a timbratura
          </div>
          <ul className="wl-team-list">
            {collaborators.map((c) => (
              <li key={c.user_id} className="wl-team-row" data-testid={`collab-row-${c.user_id}`}>
                <button
                  type="button"
                  onClick={() => setOpenUserId(c.user_id)}
                  className="shrink-0 rounded-full transition-transform hover:scale-105"
                  aria-label={`Scheda di ${c.user_name}`}
                >
                  <UserAvatar userId={c.user_id} name={c.user_name} version={c.avatar_version ?? null} size={32} />
                </button>
                <div className="wl-team-row__meta">
                  <button
                    type="button"
                    onClick={() => setOpenUserId(c.user_id)}
                    className="wl-team-row__name text-left hover:underline"
                    title={c.user_name}
                  >
                    {c.user_name}
                  </button>
                  <span className="wl-team-row__role">{(c.role === "admin" || c.role === "super_admin") ? roleLabel(c.role) : (c.job_title || roleLabel(c.role))}</span>
                </div>
                <div className="wl-team-row__right">
                  <span className="wl-badge wl-badge--info">Esterno</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <UserCardDialog userId={openUserId} onClose={() => setOpenUserId(null)} />
      {fixUser && (
        <DayDetailDialog
          userId={fixUser.user_id}
          userName={fixUser.user_name}
          date={todayISO()}
          onClose={() => setFixUser(null)}
          onChanged={() => { refresh(); onDataChanged?.(); }}
        />
      )}
    </SectionCard>
  );
};


// Riquadro admin: registra malattia o segnala assenza ingiustificata per un dipendente
const AdminAbsenceForm = ({ people = [], onCreated }) => {
  const [userId, setUserId] = useState("");
  const [type, setType] = useState("malattia");
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [certSent, setCertSent] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!userId) { toast.error("Scegli il dipendente."); return; }
    setSaving(true);
    try {
      await api.post("/attendance/records/admin", {
        user_id: userId, type, start_date: startDate, end_date: endDate,
        note: note || null, certificate_sent: type === "malattia" ? certSent : false,
      });
      toast.success(`${TYPE_LABEL[type]} registrata. Il dipendente riceverà l'avviso.`);
      setNote(""); setCertSent(false); setStartDate(todayISO()); setEndDate(todayISO());
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella registrazione.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Registra assenza (admin)" icon={Stethoscope}>
      <div className="space-y-3" data-testid="admin-absence-form">
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="h-9 rounded-md border border-border bg-surface px-2 text-sm w-full"
          data-testid="absence-user-select"
        >
          <option value="">Utente…</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="flex gap-2">
          {[["malattia", "Malattia", Stethoscope], ["assenza", "Assenza ingiustificata", AlertTriangle]].map(([v, l, Icon]) => (
            <button
              key={v}
              type="button"
              onClick={() => setType(v)}
              data-testid={`absence-type-${v}`}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-md border text-sm transition-colors ${
                type === v ? "border-accent bg-accent/10 text-accent font-medium" : "border-border text-text-secondary hover:bg-subtle"
              }`}
            >
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] text-text-muted">Dal</label>
            <DateInputIT value={startDate} onChange={(v) => { setStartDate(v); if (v && endDate && endDate < v) setEndDate(v); }}
                         className="h-9 w-full" testId="absence-start-date" />
          </div>
          <div>
            <label className="text-[11px] text-text-muted">Al</label>
            <DateInputIT value={endDate} min={startDate} onChange={setEndDate}
                         className="h-9 w-full" testId="absence-end-date" />
          </div>
        </div>
        {type === "malattia" && (
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer select-none" data-testid="absence-cert-label">
            <input type="checkbox" checked={certSent} onChange={(e) => setCertSent(e.target.checked)}
                   className="h-4 w-4 accent-[var(--accent,#2563eb)]" data-testid="absence-cert-checkbox" />
            Certificato medico inviato
          </label>
        )}
        <input
          type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300}
          placeholder="Nota (facoltativa)"
          className="h-9 rounded-md border border-border bg-surface px-3 text-sm w-full"
          data-testid="absence-note-input"
        />
        <Button onClick={submit} disabled={saving} className="w-full" data-testid="absence-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : "Registra"}
        </Button>
        <p className="text-[11px] text-text-muted">
          Registrazione immediata, senza approvazione. Il dipendente riceve l'avviso nella sua pagina Presenze.
        </p>
      </div>
    </SectionCard>
  );
};


const AdminRecordsManager = ({ refreshKey, onChanged }) => {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { id, decision, user, type }

  const load = useCallback(() => {
    setLoading(true);
    const q = filter !== "all" ? `?status=${filter}` : "";
    api.get(`/attendance/records${q}`).then(({ data }) => {
      setItems(data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const runDecision = async () => {
    if (!confirmState) return;
    const { id, decision } = confirmState;
    setDecidingId(id + decision);
    try {
      if (decision === "cancel") {
        await api.post(`/attendance/records/${id}/cancel`);
        toast.success("Registrazione annullata");
      } else {
        await api.post(`/attendance/records/${id}/decide`, { decision });
        toast.success(decision === "approved" ? "Richiesta approvata" : "Richiesta rifiutata");
      }
      setConfirmState(null);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <SectionCard
      title="Richieste del team"
      icon={CalendarDays}
      action={
        <div className="flex gap-1 text-xs">
          {[["pending", "Da decidere"], ["approved", "Approvate"], ["recorded", "Malattie/Assenze"], ["rejected", "Rifiutate"], ["all", "Tutte"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              data-testid={`filter-${v}`}
              className={`px-2.5 py-1 rounded-md ${filter === v ? "bg-accent text-white" : "bg-subtle text-text-secondary hover:bg-border"}`}
            >{l}</button>
          ))}
        </div>
      }
    >
      {loading ? (
        <div className="text-sm text-text-muted">Caricamento…</div>
      ) : !items.length ? (
        <div className="text-sm text-text-muted py-4 text-center"><Inbox size={20} className="mx-auto mb-1 opacity-60" />Nessuna richiesta</div>
      ) : (
        <ul className="divide-y divide-border" data-testid="admin-records-list">
          {items.map((r) => {
            const Icon = TYPE_ICON[r.type] || CalendarDays;
            const canDecide = r.type !== "malattia" && r.status === "pending";
            return (
              <li key={r.id} className="py-3 flex items-start gap-3" data-testid={`record-${r.id}`}>
                <Icon size={16} className="text-text-muted mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm">
                      <span className="font-medium text-text-primary">{r.user_name}</span>
                      <span className="text-text-secondary"> · {TYPE_LABEL[r.type]}</span>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-xs text-text-secondary mt-0.5">
                    {fmtRecordPeriod(r)}
                  </div>
                  {r.note && <div className="text-xs text-text-muted mt-1 italic">«{r.note}»</div>}
                  {r.created_by_admin_name && (
                    <div className="text-[11px] text-text-muted mt-1">Registrata da {r.created_by_admin_name}</div>
                  )}
                  {r.approved_by_name && <div className="text-[11px] text-text-muted mt-1">Decisione di {r.approved_by_name} · {fmtDate(r.approved_at)}</div>}
                  {r.type === "malattia" && r.status !== "cancelled" && (
                    <label className="mt-1.5 flex items-center gap-2 text-xs text-text-primary cursor-pointer select-none" data-testid={`cert-flag-label-${r.id}`}>
                      <input
                        type="checkbox"
                        checked={!!r.certificate_sent}
                        onChange={async (e) => {
                          try {
                            await api.put(`/attendance/records/${r.id}/certificate-flag`, { sent: e.target.checked });
                            toast.success(e.target.checked ? "Certificato segnato come inviato" : "Spunta rimossa");
                            load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Errore");
                          }
                        }}
                        className="h-3.5 w-3.5"
                        data-testid={`cert-flag-${r.id}`}
                      />
                      Certificato medico inviato
                      {r.certificate_sent && r.certificate_sent_by_name && (
                        <span className="text-text-muted">({r.certificate_sent_by_name} · {fmtDate(r.certificate_sent_at)})</span>
                      )}
                    </label>
                  )}
                  {r.type === "malattia" && r.certificate_file_id && (
                    <div className="mt-1.5"><CertificateDownloader record={r} testIdPrefix="admin-cert-dl" /></div>
                  )}
                  {r.status !== "cancelled" && r.status !== "rejected" && (
                    <button
                      type="button"
                      onClick={() => setConfirmState({ id: r.id, decision: "cancel", user: r.user_name, type: r.type })}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-text-muted hover:text-red-500"
                      data-testid={`cancel-record-${r.id}`}
                    >
                      <Trash2 size={11} /> Annulla registrazione
                    </button>
                  )}
                  {canDecide && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={() => setConfirmState({ id: r.id, decision: "approved", user: r.user_name, type: r.type })}
                        disabled={decidingId === r.id + "approved"}
                        data-testid={`approve-${r.id}`}
                        className="bg-green-600 hover:bg-green-700 text-white h-7 px-2.5 text-xs"
                      >
                        <CheckCircle2 size={12} className="mr-1" /> Approva
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmState({ id: r.id, decision: "rejected", user: r.user_name, type: r.type })}
                        disabled={decidingId === r.id + "rejected"}
                        data-testid={`reject-${r.id}`}
                        className="h-7 px-2.5 text-xs"
                      >
                        <XCircle size={12} className="mr-1" /> Rifiuta
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmModal
        open={!!confirmState}
        onOpenChange={(v) => { if (!v && !decidingId) setConfirmState(null); }}
        title={
          confirmState?.decision === "cancel"
            ? `Annullare la registrazione di ${confirmState?.user}?`
            : confirmState?.decision === "approved"
              ? `Approvare la richiesta di ${confirmState?.user}?`
              : `Rifiutare la richiesta di ${confirmState?.user}?`
        }
        description={
          confirmState
            ? (confirmState.decision === "cancel"
                ? `${TYPE_LABEL[confirmState.type]} sparirà da calendario, conteggi ed export (resta in archivio come "annullata"). Il dipendente riceverà l'avviso.`
                : confirmState.decision === "approved"
                ? `L'utente riceverà notifica di approvazione del ${TYPE_LABEL[confirmState.type].toLowerCase()}.`
                : `L'utente riceverà notifica di rifiuto del ${TYPE_LABEL[confirmState.type].toLowerCase()}. L'azione può essere revocata solo manualmente.`)
            : undefined
        }
        variant={confirmState?.decision === "approved" ? "primary" : "danger"}
        confirmLabel={
          confirmState?.decision === "cancel" ? "Annulla registrazione"
            : confirmState?.decision === "rejected" ? "Rifiuta richiesta" : "Approva richiesta"
        }
        cancelLabel="Annulla"
        loading={!!decidingId}
        onConfirm={runDecision}
        testId="confirm-decision"
      />
    </SectionCard>
  );
};


const AdminNotifications = ({ initial, onMarkedRead }) => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  const load = useCallback(() => {
    api.get("/attendance/notifications/me?unread_only=true&limit=20").then(({ data }) => setItems(data || []));
    api.get("/attendance/notifications/unread-count").then(({ data }) => setCount(data?.count || 0));
  }, []);

  useEffect(() => {
    if (initial) { setItems(initial.notifications || []); setCount(initial.unread_count || 0); }
  }, [initial]);

  const markAll = async () => {
    try {
      await api.post("/attendance/notifications/read-all");
      toast.success("Notifiche segnate come lette");
      load();
      onMarkedRead && onMarkedRead();
    } catch (e) {
      toast.error("Errore");
    }
  };

  if (count === 0) return null;

  return (
    <SectionCard
      title={`Nuove notifiche (${count})`}
      icon={Bell}
      action={<button onClick={markAll} data-testid="mark-all-read" className="text-xs text-accent hover:underline">Segna tutte come lette</button>}
    >
      <ul className="space-y-1.5 text-sm">
        {items.slice(0, 5).map((n) => (
          <li key={n.id} className="text-text-secondary">
            <span className="font-medium text-text-primary">{n.actor_user_name}</span>{" "}
            ha {n.kind === "attendance_sickness_recorded" ? "comunicato" : "richiesto"}{" "}
            <span className="text-text-primary">{TYPE_LABEL[n.actor_type]}</span>
            <span className="text-text-muted text-xs"> · {fmtDate(n.created_at)}</span>
          </li>
        ))}
        {items.length > 5 && <li className="text-xs text-text-muted">+ altre {items.length - 5} notifiche…</li>}
      </ul>
    </SectionCard>
  );
};


// ───────────────────────────────────────────────────────────────────
// Persone fuori piattaforma (solo per il Foglio presenze del consulente)
// ───────────────────────────────────────────────────────────────────

const ExternalPeopleManager = ({ initial }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: "", matricola: "", contract_daily_hours: "8" });
  const [editingId, setEditingId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/attendance/external-people")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (initial) { setItems(initial); setLoading(false); }
  }, [initial]);

  const resetForm = () => {
    setForm({ full_name: "", matricola: "", contract_daily_hours: "8" });
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("Inserisci il nome.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        matricola: form.matricola.trim() || null,
        contract_daily_hours: Number(form.contract_daily_hours) || 8,
      };
      if (editingId) {
        await api.patch(`/attendance/external-people/${editingId}`, payload);
        toast.success("Persona aggiornata.");
      } else {
        await api.post("/attendance/external-people", payload);
        toast.success("Persona aggiunta.");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Operazione non riuscita.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      full_name: p.full_name || "",
      matricola: p.matricola || "",
      contract_daily_hours: String(p.contract_daily_hours ?? 8),
    });
  };

  const doDelete = async () => {
    try {
      await api.delete(`/attendance/external-people/${toDelete.id}`);
      toast.success("Persona rimossa.");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Operazione non riuscita.");
    }
  };

  return (
    <SectionCard title="Persone fuori piattaforma" icon={Users}>
      <p className="text-xs text-text-muted mb-3" data-testid="external-people-hint">
        Compaiono solo nel <b>Foglio presenze — consulente del lavoro</b>, con il blocco vuoto da compilare a mano.
        Non hanno accesso a Wattline e non timbrano.
      </p>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 mb-4" data-testid="external-people-form">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-text-secondary mb-1">Nome e cognome</label>
          <input
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            maxLength={80}
            placeholder="es. Rossi Mario"
            className="w-full px-3 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
            data-testid="external-person-name"
          />
        </div>
        <div className="w-28">
          <label className="block text-xs font-medium text-text-secondary mb-1">Matricola</label>
          <input
            value={form.matricola}
            onChange={(e) => setForm({ ...form, matricola: e.target.value })}
            maxLength={30}
            placeholder="es. 005"
            className="w-full px-3 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
            data-testid="external-person-matricola"
          />
        </div>
        <div className="w-32">
          <label className="block text-xs font-medium text-text-secondary mb-1">Ore/giorno</label>
          <input
            type="number"
            min={0.5}
            max={24}
            step={0.5}
            value={form.contract_daily_hours}
            onChange={(e) => setForm({ ...form, contract_daily_hours: e.target.value })}
            className="w-full px-3 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
            data-testid="external-person-hours"
          />
        </div>
        <Button type="submit" disabled={saving} className="bg-accent hover:bg-accent/90 text-white h-9" data-testid="external-person-submit">
          {saving ? <Loader2 size={14} className="animate-spin" /> : editingId ? "Salva" : <><Plus size={14} className="mr-1" /> Aggiungi</>}
        </Button>
        {editingId && (
          <Button type="button" variant="outline" className="h-9" onClick={resetForm} data-testid="external-person-cancel-edit">
            Annulla
          </Button>
        )}
      </form>
      {loading ? (
        <div className="text-xs text-text-muted py-2"><Loader2 size={12} className="inline animate-spin mr-1" />Carico…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-text-muted bg-subtle border border-border rounded-md px-3 py-2" data-testid="external-people-empty">
          Nessuna persona fuori piattaforma.
        </div>
      ) : (
        <div className="divide-y divide-border border border-border rounded-md" data-testid="external-people-list">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-3 py-2" data-testid={`external-person-row-${p.id}`}>
              <div className="min-w-0">
                <span className="text-sm font-medium text-text-primary">{p.full_name}</span>
                <span className="text-xs text-text-muted ml-2">
                  {p.matricola ? `matr. ${p.matricola} · ` : ""}{String(p.contract_daily_hours).replace(".", ",")} h/giorno
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => startEdit(p)} className="p-1.5 text-text-muted hover:text-accent" data-testid={`external-person-edit-${p.id}`}>
                  <Pencil size={14} />
                </button>
                <button type="button" onClick={() => setToDelete(p)} className="p-1.5 text-text-muted hover:text-danger" data-testid={`external-person-delete-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmModal
        open={!!toDelete}
        onOpenChange={(v) => { if (!v) setToDelete(null); }}
        onConfirm={doDelete}
        title="Rimuovere la persona?"
        description={`${toDelete?.full_name || ""} non comparirà più nel Foglio presenze del consulente del lavoro.`}
        confirmLabel="Rimuovi"
        variant="danger"
        testId="external-person-delete-confirm"
      />
    </SectionCard>
  );
};


// ───────────────────────────────────────────────────────────────────
// Export mensile (.xlsx) — admin
// ───────────────────────────────────────────────────────────────────

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const ExportMonthModal = ({ open, onClose }) => {
  const today = new Date();
  const [format, setFormat] = useState("daily"); // 'daily' | 'payroll'
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [fromYear, setFromYear] = useState(today.getFullYear());
  const [fromMonth, setFromMonth] = useState(today.getMonth() + 1);
  const [toYear, setToYear] = useState(today.getFullYear());
  const [toMonth, setToMonth] = useState(today.getMonth() + 1);
  const [scope, setScope] = useState("all"); // 'all' | 'one'
  const [userId, setUserId] = useState("");
  const [team, setTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingTeam(true);
    // Riutilizziamo l'endpoint calendar per ottenere la lista membri dell'azienda
    api.get(`/attendance/calendar?year=${year}&month=${month}`)
      .then(({ data }) => {
        const members = (data?.team || [])
          .filter((m) => m.role !== "super_admin")
          .map((m) => ({ id: m.user_id, name: m.user_name, email: m.user_email }));
        setTeam(members);
      })
      .catch(() => setTeam([]))
      .finally(() => setLoadingTeam(false));
  }, [open, year, month]);

  if (!open) return null;

  const downloadBlob = (res, fallbackName) => {
    const disp = res.headers?.["content-disposition"] || "";
    const match = disp.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : fallbackName;
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    if (format === "payroll") {
      const span = (toYear - fromYear) * 12 + (toMonth - fromMonth);
      if (span < 0) {
        toast.error("L'intervallo non è valido: il mese di fine precede quello di inizio.");
        return;
      }
      if (span >= 12) {
        toast.error("Massimo 12 mesi per file.");
        return;
      }
      setDownloading(true);
      try {
        const params = new URLSearchParams({
          year_from: String(fromYear), month_from: String(fromMonth),
          year_to: String(toYear), month_to: String(toMonth),
        });
        const res = await api.get(`/attendance/export-payroll-sheet?${params.toString()}`, { responseType: "blob" });
        downloadBlob(res, `Foglio_presenze_${fromYear}-${String(fromMonth).padStart(2, "0")}.xlsx`);
        toast.success("Foglio presenze scaricato.");
        onClose();
      } catch (err) {
        toast.error("Impossibile generare il foglio presenze.");
      } finally {
        setDownloading(false);
      }
      return;
    }
    if (scope === "one" && !userId) {
      toast.error("Seleziona un utente.");
      return;
    }
    setDownloading(true);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (scope === "one") params.append("user_id", userId);
      const res = await api.get(`/attendance/export-month?${params.toString()}`, { responseType: "blob" });
      downloadBlob(res, `Presenze_${year}-${String(month).padStart(2, "0")}.xlsx`);
      toast.success("Export scaricato.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile generare l'export.");
    } finally {
      setDownloading(false);
    }
  };

  const yearOptions = [today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="export-month-modal">
      <div className="bg-surface border border-border rounded-lg w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-accent" />
            <h3 className="text-sm font-semibold">Esporta presenze del mese</h3>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="close-export">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Formato</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormat("daily")}
                data-testid="export-format-daily"
                className={`flex-1 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                  format === "daily" ? "bg-accent text-white border-accent" : "bg-subtle border-border text-text-secondary hover:border-accent"
                }`}
              >
                Dettaglio giornaliero
              </button>
              <button
                type="button"
                onClick={() => setFormat("payroll")}
                data-testid="export-format-payroll"
                className={`flex-1 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                  format === "payroll" ? "bg-accent text-white border-accent" : "bg-subtle border-border text-text-secondary hover:border-accent"
                }`}
              >
                Foglio presenze — consulente del lavoro
              </button>
            </div>
          </div>

          {format === "payroll" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1.5">Da</label>
                <div className="flex gap-2">
                  <select
                    value={fromMonth}
                    onChange={(e) => setFromMonth(Number(e.target.value))}
                    data-testid="payroll-from-month"
                    className="flex-1 min-w-0 px-2 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
                  >
                    {MONTHS_IT.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={fromYear}
                    onChange={(e) => setFromYear(Number(e.target.value))}
                    data-testid="payroll-from-year"
                    className="w-20 px-2 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1.5">A</label>
                <div className="flex gap-2">
                  <select
                    value={toMonth}
                    onChange={(e) => setToMonth(Number(e.target.value))}
                    data-testid="payroll-to-month"
                    className="flex-1 min-w-0 px-2 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
                  >
                    {MONTHS_IT.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={toYear}
                    onChange={(e) => setToYear(Number(e.target.value))}
                    data-testid="payroll-to-year"
                    className="w-20 px-2 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {format === "daily" && (
            <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Mese</label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                data-testid="export-month-select"
                className="w-full px-3 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
              >
                {MONTHS_IT.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Anno</label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                data-testid="export-year-select"
                className="w-full px-3 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Ambito</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setScope("all"); setUserId(""); }}
                data-testid="export-scope-all"
                className={`flex-1 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                  scope === "all" ? "bg-accent text-white border-accent" : "bg-subtle border-border text-text-secondary hover:border-accent"
                }`}
              >
                Tutta l'azienda
              </button>
              <button
                type="button"
                onClick={() => setScope("one")}
                data-testid="export-scope-one"
                className={`flex-1 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                  scope === "one" ? "bg-accent text-white border-accent" : "bg-subtle border-border text-text-secondary hover:border-accent"
                }`}
              >
                Un singolo utente
              </button>
            </div>
          </div>

          {scope === "one" && (
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Utente</label>
              {loadingTeam ? (
                <div className="text-xs text-text-muted py-2"><Loader2 size={12} className="inline animate-spin mr-1" />Carico team…</div>
              ) : team.length === 0 ? (
                <div className="text-xs text-text-muted bg-subtle border border-border rounded-md px-3 py-2">
                  Nessun utente con presenze/assenze nel mese.
                </div>
              ) : (
                <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  data-testid="export-user-select"
                  className="w-full px-3 py-2 bg-subtle border border-border rounded-md text-sm focus:outline-none focus:border-accent"
                >
                  <option value="">— Seleziona —</option>
                  {team.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
                  ))}
                </select>
              )}
            </div>
          )}
            </>
          )}

          <div className="text-[11px] text-text-muted bg-subtle/50 border border-border rounded-md p-3">
            {format === "daily" ? (
              <>
                Il file contiene 2 fogli: <b>Dettaglio giornaliero</b> (una riga per persona × giorno) e <b>Riepilogo mensile</b> (totali per persona).
                Le note per le malattie riportano solo &quot;Malattia&quot; o &quot;Certificato allegato&quot;, mai diagnosi.
              </>
            ) : (
              <>
                Formato per il consulente del lavoro: un foglio per mese, blocco per persona
                (<b>OPERATIVO · FERIE · PERMESSO · MALATTIA · NOTE</b>), ore in decimale e totali con formule attive.
                Include le persone fuori piattaforma e gli esenti da timbratura (blocco da compilare a mano).
              </>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={downloading} data-testid="cancel-export">
            Annulla
          </Button>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={downloading || (format === "daily" && scope === "one" && !userId)}
            data-testid="confirm-export-btn"
            className="bg-accent hover:bg-accent/90 text-white"
          >
            {downloading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}
            Scarica Excel
          </Button>
        </div>
      </div>
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────
// Page root
// ───────────────────────────────────────────────────────────────────

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [refreshKey, setRefreshKey] = useState(0);
  const [showExport, setShowExport] = useState(false);
  const bump = () => setRefreshKey((k) => k + 1);
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const calPrev = () => { if (calMonth === 1) { setCalMonth(12); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); };
  const calNext = () => { if (calMonth === 12) { setCalMonth(1); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); };

  // Una chiamata sola per i dati di apertura della pagina; i componenti la
  // ricevono via props e rifanno le loro chiamate puntuali solo dopo azioni.
  const [pagina, setPagina] = useState(null);
  const [paginaFailed, setPaginaFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setPaginaFailed(false);
    api.get("/attendance/pagina")
      .then(({ data }) => { if (!cancelled) setPagina(data); })
      .catch(() => { if (!cancelled) setPaginaFailed(true); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const teamToday = pagina?.admin?.team_today;
  const peopleAssenze = [...(teamToday?.team || []), ...(teamToday?.collaborators || [])]
    .map((u) => ({ id: u.user_id, name: u.user_name }));

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="attendance-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Presenze & Assenze</h1>
          <p className="text-sm text-text-muted mt-1">
            Timbratura giornaliera e gestione assenze dell'azienda.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setShowExport(true)}
            data-testid="open-export-month-btn"
            variant="outline"
            className="border-accent text-accent hover:bg-accent/10"
          >
            <FileSpreadsheet size={14} className="mr-1.5" /> Esporta mese
          </Button>
        )}
      </div>

      {paginaFailed && !pagina ? (
        <div className="bg-surface border border-border rounded-xl">
          <LoadErrorBox what="le presenze" onRetry={bump} testId="attendance-load-error" />
        </div>
      ) : (
      <>
      {/* Avviso correzioni timbrature (per il dipendente interessato) */}
      <EmployeeClockNotices initial={pagina?.user_notices} />

      {/* Timbratura propria (nascosta per i collaboratori non soggetti a timbratura) */}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        {!user?.attendance_exempt && <MyClockToday initial={pagina?.me_today} />}
        <CreateRecordForm onCreated={bump} />
      </div>

      {isAdmin && (
        <>
          <div className="mb-5"><AdminNotifications initial={pagina?.admin} onMarkedRead={bump} /></div>
          <div className="mb-5"><TeamCalendar refreshKey={refreshKey} year={calYear} month={calMonth} onPrev={calPrev} onNext={calNext} onDataChanged={bump} /></div>
          <div className="mb-5"><MonthDiary refreshKey={refreshKey} year={calYear} month={calMonth} /></div>
          <div className="grid lg:grid-cols-2 gap-5 mb-5">
            <AdminTeamToday initial={teamToday} onDataChanged={bump} />
            <AdminAbsenceForm people={peopleAssenze} onCreated={bump} />
          </div>
          <div className="mb-5">
            <AdminRecordsManager refreshKey={refreshKey} onChanged={bump} />
          </div>
          <div className="mb-5">
            <ExternalPeopleManager initial={pagina?.admin?.external_people} />
          </div>
        </>
      )}

      {/* Storico personale (sotto per tutti) */}
      <SectionCard title={isAdmin ? "Le mie segnalazioni" : "Le mie richieste"} icon={CalendarDays}>
        <MyRecordsList items={pagina?.my_records} />
      </SectionCard>
      </>
      )}

      {isAdmin && (
        <ExportMonthModal open={showExport} onClose={() => setShowExport(false)} />
      )}
    </div>
  );
}
